import React, { useState, useMemo, useEffect } from "react";
import { Table, Row, Col, Modal, Spinner } from "react-bootstrap";
import Footer from "../layouts/Footer";
import {
  fetchGsmGateways,
  fetchGsmLocations,
  fetchGsmAssignments,
  addGsmAssignment,
  deleteGsmAssignment,
  fetchGsmClients,
  saveGsmClientUrls
} from "../services/api";
import { GsmStyles, GsmToasts, useNotifications, isValidUrl, formatDate } from "./GsmShared";

const EMPTY_ASSIGNMENT = { business_id: '', location: '', gateway_id: '', port_number: '' };

export default function GsmAffectations() {
  const [activeTab, setActiveTab] = useState('assignments');
  const [gateways, setGateways] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filterText, setFilterText] = useState('');

  // Réservation de port
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState(EMPTY_ASSIGNMENT);
  const [assignSaving, setAssignSaving] = useState(false);

  // Libération de port
  const [assignmentToRelease, setAssignmentToRelease] = useState(null);
  const [releasing, setReleasing] = useState(false);

  // URLs client
  const [urlClient, setUrlClient] = useState(null);
  const [urlForm, setUrlForm] = useState({ callback_url: '', auth_url: '' });
  const [urlSubmitted, setUrlSubmitted] = useState(false);
  const [urlSaving, setUrlSaving] = useState(false);

  const { notifications, showNotification, hideNotification } = useNotifications();

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) setRefreshing(true);
      else setLoading(true);
      setError("");

      const token = localStorage.getItem('authToken');
      const [gatewaysData, assignmentsData, clientsData, locationsData] = await Promise.all([
        fetchGsmGateways(token),
        fetchGsmAssignments(token),
        fetchGsmClients(token),
        fetchGsmLocations(token)
      ]);

      setGateways(gatewaysData);
      setLocations(locationsData);
      setAssignments(assignmentsData);
      setClients(clientsData.map(c => ({
        ...c,
        business_name: c.business_name || `Client ${c.business_id}`
      })));
      if (showRefreshSpinner) showNotification('Données actualisées', 'success', 3000);
    } catch (err) {
      setError(err.message || "Erreur lors du chargement des données GSM");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ============================================
  // DONNÉES DÉRIVÉES
  // ============================================
  const gatewayById = useMemo(() => {
    const map = {};
    gateways.forEach(g => { map[String(g.id)] = g; });
    return map;
  }, [gateways]);

  const clientById = useMemo(() => {
    const map = {};
    clients.forEach(c => { map[String(c.business_id)] = c; });
    return map;
  }, [clients]);

  const usedPortsByGateway = useMemo(() => {
    const map = {};
    assignments.forEach(a => {
      const key = String(a.gateway_id);
      if (!map[key]) map[key] = new Set();
      map[key].add(Number(a.port_number));
    });
    return map;
  }, [assignments]);

  const freePorts = (gateway) => {
    const used = usedPortsByGateway[String(gateway.id)] || new Set();
    const ports = [];
    for (let p = 1; p <= Number(gateway.ports_count); p++) {
      if (!used.has(p)) ports.push(p);
    }
    return ports;
  };


  const clientName = (businessId, fallback) =>
    clientById[String(businessId)]?.business_name || fallback || `Client ${businessId}`;

  const enrichedAssignments = useMemo(() => {
    const search = filterText.trim().toLowerCase();
    return assignments
      .map(a => {
        const gw = gatewayById[String(a.gateway_id)] || {};
        return {
          ...a,
          client_name: clientName(a.business_id, a.business_name),
          gateway_internal_id: gw.internal_id || a.gateway_internal_id || `#${a.gateway_id}`,
          location: gw.location || a.location || '—'
        };
      })
      .filter(a => !search || [a.client_name, a.gateway_internal_id, a.location, a.port_number]
        .some(v => String(v ?? '').toLowerCase().includes(search)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, gatewayById, clientById, filterText]);

  const clientRows = useMemo(() => {
    const search = filterText.trim().toLowerCase();
    const portsCount = {};
    assignments.forEach(a => { portsCount[String(a.business_id)] = (portsCount[String(a.business_id)] || 0) + 1; });
    return clients
      .map(c => ({
        ...c,
        ports: portsCount[String(c.business_id)] || 0,
        callback_url: c.callback_url || '',
        auth_url: c.auth_url || ''
      }))
      .filter(c => !search || [c.business_name, c.callback_url, c.auth_url]
        .some(v => String(v).toLowerCase().includes(search)))
      .sort((a, b) => b.ports - a.ports || a.business_name.localeCompare(b.business_name));
  }, [clients, assignments, filterText]);

  // ============================================
  // RÉSERVATION D'UN PORT
  // ============================================
  const gatewaysForLocation = useMemo(
    () => gateways.filter(g => !assignForm.location || g.location === assignForm.location),
    [gateways, assignForm.location]
  );

  const selectedGateway = gatewayById[String(assignForm.gateway_id)];
  const availablePorts = selectedGateway ? freePorts(selectedGateway) : [];

  const openAssignModal = (businessId = '') => {
    setAssignForm({ ...EMPTY_ASSIGNMENT, business_id: businessId ? String(businessId) : '' });
    setShowAssignModal(true);
  };

  const handleAssign = async () => {
    const { business_id, gateway_id, port_number } = assignForm;
    if (!business_id || !gateway_id || !port_number) return;

    try {
      setAssignSaving(true);
      const token = localStorage.getItem('authToken');
      await addGsmAssignment(token, {
        business_id,
        gateway_id,
        port_number: Number(port_number)
      });
      showNotification(
        `Port ${port_number} de ${selectedGateway?.internal_id} réservé pour ${clientName(business_id)}`,
        'success'
      );
      setShowAssignModal(false);
      await loadData();

      // Inviter à définir les URLs si le client n'en a pas encore
      const config = clientById[String(business_id)];
      if (!config?.callback_url || !config?.auth_url) {
        openUrlModal({ business_id, business_name: clientName(business_id), ...config });
      }
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setAssignSaving(false);
    }
  };

  const handleRelease = async () => {
    if (!assignmentToRelease) return;
    try {
      setReleasing(true);
      const token = localStorage.getItem('authToken');
      await deleteGsmAssignment(token, assignmentToRelease.id);
      showNotification(`Port ${assignmentToRelease.port_number} libéré`, 'success');
      setAssignmentToRelease(null);
      await loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setReleasing(false);
    }
  };

  // ============================================
  // URLs CLIENT
  // ============================================
  const openUrlModal = (client) => {
    setUrlClient(client);
    setUrlForm({ callback_url: client.callback_url || '', auth_url: client.auth_url || '' });
    setUrlSubmitted(false);
  };

  const urlErrors = {
    callback_url: isValidUrl(urlForm.callback_url) ? null : 'URL de callback invalide (http:// ou https://)',
    auth_url: isValidUrl(urlForm.auth_url) ? null : "URL d'authentification invalide (http:// ou https://)"
  };

  const handleSaveUrls = async () => {
    setUrlSubmitted(true);
    if (urlErrors.callback_url || urlErrors.auth_url) return;

    try {
      setUrlSaving(true);
      const token = localStorage.getItem('authToken');
      await saveGsmClientUrls(token, urlClient.business_id, {
        callback_url: urlForm.callback_url.trim(),
        auth_url: urlForm.auth_url.trim()
      });
      showNotification(`URLs enregistrées pour ${urlClient.business_name}`, 'success');
      setUrlClient(null);
      await loadData();
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setUrlSaving(false);
    }
  };

  const stats = {
    reserved: assignments.length,
    free: gateways.reduce((sum, g) => sum + freePorts(g).length, 0),
    clients: new Set(assignments.map(a => String(a.business_id))).size,
    configured: clientRows.filter(c => c.callback_url && c.auth_url).length
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="gsm-page main main-app p-3 p-lg-4">
      <GsmStyles />
      <GsmToasts notifications={notifications} onClose={hideNotification} />

      {/* ========== HEADER ========== */}
      <div className="gsm-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-header-icon">
              <i className="ri-links-line"></i>
            </div>
            <div>
              <h3 className="gsm-header-title mb-1">Affectations clients</h3>
              <p className="gsm-header-subtitle mb-0">Réservation de ports GSM et URLs de callback / authentification</p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="gsm-btn-secondary" onClick={() => loadData(true)} disabled={refreshing}>
              <i className={`bi ${refreshing ? 'bi-arrow-repeat gsm-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
            <button className="gsm-btn-primary" onClick={() => openAssignModal()} disabled={gateways.length === 0}>
              <i className="bi bi-plus-lg"></i>
              <span>Réserver un port</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="gsm-alert gsm-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <button className="gsm-btn-secondary" onClick={() => loadData(true)}>Réessayer</button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        {[
          { label: 'Ports réservés', value: stats.reserved, icon: 'ri-lock-line', color: 'amber' },
          { label: 'Ports libres', value: stats.free, icon: 'ri-sim-card-2-line', color: 'green' },
          { label: 'Clients affectés', value: stats.clients, icon: 'ri-user-line', color: 'blue' },
          { label: 'Clients avec URLs', value: stats.configured, icon: 'ri-link', color: 'violet' }
        ].map(s => (
          <Col xs={6} lg={3} key={s.label}>
            <div className="gsm-stat-card">
              <div className={`gsm-stat-icon gsm-icon-${s.color}`}><i className={s.icon}></i></div>
              <div>
                <div className="gsm-stat-label">{s.label}</div>
                <div className="gsm-stat-value">{s.value}</div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ========== ONGLETS + RECHERCHE ========== */}
      <div className="gsm-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg="auto">
            <div className="gsm-tabs">
              <button className={`gsm-tab ${activeTab === 'assignments' ? 'active' : ''}`} onClick={() => setActiveTab('assignments')}>
                <i className="ri-sim-card-2-line"></i>Ports réservés
              </button>
              <button className={`gsm-tab ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')}>
                <i className="ri-link"></i>URLs clients
              </button>
            </div>
          </Col>
          <Col>
            <div className="gsm-search-wrapper">
              <i className="bi bi-search gsm-search-icon"></i>
              <input
                type="text"
                className="gsm-search-input"
                placeholder={activeTab === 'assignments'
                  ? 'Rechercher par client, passerelle, localisation, port...'
                  : 'Rechercher par client ou URL...'}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* ========== TABLES ========== */}
      <div className="gsm-table-card mb-4">
        {loading ? (
          <div className="gsm-empty"><Spinner animation="border" /><div className="mt-2">Chargement...</div></div>
        ) : activeTab === 'assignments' ? (
          enrichedAssignments.length === 0 ? (
            <div className="gsm-empty"><i className="ri-sim-card-2-line"></i>Aucun port réservé</div>
          ) : (
            <div className="table-responsive">
              <Table className="gsm-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Passerelle</th>
                    <th>Localisation</th>
                    <th>Port</th>
                    <th>Réservé le</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {enrichedAssignments.map(a => (
                    <tr key={a.id}>
                      <td className="fw-semibold">{a.client_name}</td>
                      <td><span className="gsm-code">{a.gateway_internal_id}</span></td>
                      <td><span className="gsm-badge gsm-badge-gray"><i className="ri-map-pin-line"></i>{a.location}</span></td>
                      <td><span className="gsm-badge gsm-badge-amber">Port {a.port_number}</span></td>
                      <td className="text-muted">{formatDate(a.created_at)}</td>
                      <td className="text-end">
                        <button className="gsm-action-btn gsm-action-delete" title="Libérer le port" onClick={() => setAssignmentToRelease(a)}>
                          <i className="bi bi-unlock"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )
        ) : clientRows.length === 0 ? (
          <div className="gsm-empty"><i className="ri-user-line"></i>Aucun client</div>
        ) : (
          <div className="table-responsive">
            <Table className="gsm-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Ports réservés</th>
                  <th>URL de callback</th>
                  <th>URL d'authentification</th>
                  <th>Statut</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clientRows.map(c => {
                  const configured = c.callback_url && c.auth_url;
                  return (
                    <tr key={c.business_id}>
                      <td className="fw-semibold">{c.business_name}</td>
                      <td>{c.ports}</td>
                      <td><span className="gsm-url">{c.callback_url || '—'}</span></td>
                      <td><span className="gsm-url">{c.auth_url || '—'}</span></td>
                      <td>
                        {configured
                          ? <span className="gsm-badge gsm-badge-green">Configuré</span>
                          : <span className={`gsm-badge ${c.ports > 0 ? 'gsm-badge-red' : 'gsm-badge-gray'}`}>Non configuré</span>}
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-2">
                          <button className="gsm-action-btn gsm-action-edit" title="Réserver un port" onClick={() => openAssignModal(c.business_id)}>
                            <i className="ri-sim-card-2-line"></i>
                          </button>
                          <button className="gsm-action-btn gsm-action-edit" title="Définir les URLs" onClick={() => openUrlModal(c)}>
                            <i className="bi bi-pencil"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      {/* ========== MODAL RÉSERVATION ========== */}
      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg" centered className="gsm-modal">
        <div className="gsm-modal-header gsm-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-modal-icon"><i className="ri-sim-card-2-line"></i></div>
            <div>
              <h5 className="gsm-modal-title">Réserver un port</h5>
              <small className="gsm-modal-subtitle">Affecter un port de passerelle GSM à un client</small>
            </div>
          </div>
          <button className="gsm-modal-close" onClick={() => setShowAssignModal(false)}><i className="bi bi-x-lg"></i></button>
        </div>

        <Modal.Body className="gsm-modal-body">
          <Row className="g-3">
            <Col md={12}>
              <label className="gsm-form-label">Client <span className="gsm-required">*</span></label>
              <select
                className="gsm-select"
                value={assignForm.business_id}
                onChange={(e) => setAssignForm({ ...assignForm, business_id: e.target.value })}
              >
                <option value="">Sélectionner un client</option>
                {clients.map(c => <option key={c.business_id} value={c.business_id}>{c.business_name}</option>)}
              </select>
            </Col>
            <Col md={6}>
              <label className="gsm-form-label">Localisation</label>
              <select
                className="gsm-select"
                value={assignForm.location}
                onChange={(e) => setAssignForm({ ...assignForm, location: e.target.value, gateway_id: '', port_number: '' })}
              >
                <option value="">Toutes les localisations</option>
                {locations.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </Col>
            <Col md={6}>
              <label className="gsm-form-label">Passerelle <span className="gsm-required">*</span></label>
              <select
                className="gsm-select"
                value={assignForm.gateway_id}
                onChange={(e) => setAssignForm({ ...assignForm, gateway_id: e.target.value, port_number: '' })}
              >
                <option value="">Sélectionner une passerelle</option>
                {gatewaysForLocation.map(g => {
                  const free = freePorts(g).length;
                  return (
                    <option key={g.id} value={g.id} disabled={free === 0}>
                      {g.internal_id} — {g.location} ({free} port{free > 1 ? 's' : ''} libre{free > 1 ? 's' : ''})
                    </option>
                  );
                })}
              </select>
            </Col>
            <Col md={12}>
              <label className="gsm-form-label">Port <span className="gsm-required">*</span></label>
              <select
                className="gsm-select"
                value={assignForm.port_number}
                disabled={!selectedGateway}
                onChange={(e) => setAssignForm({ ...assignForm, port_number: e.target.value })}
              >
                <option value="">{selectedGateway ? 'Sélectionner un port libre' : "Choisissez d'abord une passerelle"}</option>
                {availablePorts.map(p => <option key={p} value={p}>Port {p}</option>)}
              </select>
              {selectedGateway && (
                <div className="gsm-form-hint">
                  <i className="bi bi-info-circle"></i>
                  IP locale {selectedGateway.local_ip} · IP distante {selectedGateway.remote_ip}
                </div>
              )}
            </Col>
          </Row>
        </Modal.Body>

        <div className="gsm-modal-footer">
          <button className="gsm-btn-cancel" onClick={() => setShowAssignModal(false)}>Annuler</button>
          <button
            className="gsm-btn-success"
            onClick={handleAssign}
            disabled={assignSaving || !assignForm.business_id || !assignForm.gateway_id || !assignForm.port_number}
          >
            {assignSaving ? <><Spinner animation="border" size="sm" /> Réservation...</> : <><i className="bi bi-check-lg"></i> Réserver</>}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL LIBÉRATION ========== */}
      <Modal show={!!assignmentToRelease} onHide={() => setAssignmentToRelease(null)} centered className="gsm-modal">
        <div className="gsm-modal-header gsm-modal-header-danger">
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-modal-icon"><i className="bi bi-unlock"></i></div>
            <h5 className="gsm-modal-title">Libérer le port</h5>
          </div>
          <button className="gsm-modal-close" onClick={() => setAssignmentToRelease(null)}><i className="bi bi-x-lg"></i></button>
        </div>
        <Modal.Body className="gsm-modal-body">
          {assignmentToRelease && (
            <p className="mb-0">
              Libérer le <strong>port {assignmentToRelease.port_number}</strong> de la passerelle{' '}
              <strong>{assignmentToRelease.gateway_internal_id}</strong> ({assignmentToRelease.location}) réservé par{' '}
              <strong>{assignmentToRelease.client_name}</strong> ?
            </p>
          )}
        </Modal.Body>
        <div className="gsm-modal-footer">
          <button className="gsm-btn-cancel" onClick={() => setAssignmentToRelease(null)}>Annuler</button>
          <button className="gsm-btn-danger" onClick={handleRelease} disabled={releasing}>
            {releasing ? <><Spinner animation="border" size="sm" /> Libération...</> : <><i className="bi bi-unlock"></i> Libérer</>}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL URLs CLIENT ========== */}
      <Modal show={!!urlClient} onHide={() => setUrlClient(null)} size="lg" centered className="gsm-modal">
        <div className="gsm-modal-header gsm-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="gsm-modal-icon"><i className="ri-link"></i></div>
            <div>
              <h5 className="gsm-modal-title">URLs du client</h5>
              <small className="gsm-modal-subtitle">{urlClient?.business_name}</small>
            </div>
          </div>
          <button className="gsm-modal-close" onClick={() => setUrlClient(null)}><i className="bi bi-x-lg"></i></button>
        </div>

        <Modal.Body className="gsm-modal-body">
          <Row className="g-3">
            <Col md={12}>
              <label className="gsm-form-label">URL de callback <span className="gsm-required">*</span></label>
              <input
                className={`gsm-input ${urlSubmitted && urlErrors.callback_url ? 'is-invalid' : ''}`}
                value={urlForm.callback_url}
                onChange={(e) => setUrlForm({ ...urlForm, callback_url: e.target.value })}
                placeholder="https://client.example.com/gsm/callback"
              />
              {urlSubmitted && urlErrors.callback_url
                ? <div className="gsm-form-error">{urlErrors.callback_url}</div>
                : <div className="gsm-form-hint"><i className="bi bi-info-circle"></i>Appelée pour notifier le client des événements GSM</div>}
            </Col>
            <Col md={12}>
              <label className="gsm-form-label">URL d'authentification <span className="gsm-required">*</span></label>
              <input
                className={`gsm-input ${urlSubmitted && urlErrors.auth_url ? 'is-invalid' : ''}`}
                value={urlForm.auth_url}
                onChange={(e) => setUrlForm({ ...urlForm, auth_url: e.target.value })}
                placeholder="https://client.example.com/gsm/auth"
              />
              {urlSubmitted && urlErrors.auth_url
                ? <div className="gsm-form-error">{urlErrors.auth_url}</div>
                : <div className="gsm-form-hint"><i className="bi bi-info-circle"></i>Utilisée pour authentifier les échanges avec le client</div>}
            </Col>
          </Row>
        </Modal.Body>

        <div className="gsm-modal-footer">
          <button className="gsm-btn-cancel" onClick={() => setUrlClient(null)}>Annuler</button>
          <button className="gsm-btn-success" onClick={handleSaveUrls} disabled={urlSaving}>
            {urlSaving ? <><Spinner animation="border" size="sm" /> Enregistrement...</> : <><i className="bi bi-check-lg"></i> Enregistrer</>}
          </button>
        </div>
      </Modal>

      <Footer />
    </div>
  );
}
